import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { importLibrary } from '@googlemaps/js-api-loader';
import { BaseFormControl, BaseSignalInput } from '@vallift/ngl';
import { configureGoogleMaps } from '@core/config/google-maps';

export interface GoogleAddressValue {
  /** Google list label (main + secondary), used in data list. */
  display?: string;
  /** Legacy optional secondary line; new answers leave this unset. */
  secondaryDisplay?: string;
  /** Full formatted address from Place Details. */
  address: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string | null;
}

function formattableText(
  value: google.maps.places.FormattableText | string | null | undefined,
): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (value && typeof value === 'object' && 'text' in value) {
    const text = (value as google.maps.places.FormattableText).text;
    return typeof text === 'string' ? text.trim() : '';
  }
  return '';
}

/** Single label matching the Places dropdown item. */
function predictionListDisplay(
  prediction: google.maps.places.PlacePrediction,
): string {
  const main = formattableText(prediction.mainText);
  const secondary = formattableText(prediction.secondaryText);
  const joined = [main, secondary].filter(Boolean).join(', ');
  return joined || formattableText(prediction.text);
}

function placeFullAddressText(
  place: google.maps.places.Place,
  fallbackDisplay: string,
): string {
  return (
    place.formattedAddress?.trim() ||
    place.shortFormattedAddress?.trim() ||
    place.displayName?.trim() ||
    fallbackDisplay ||
    ''
  );
}

function widgetLabelFromValue(value: GoogleAddressValue): string {
  return value.display?.trim() || value.address.trim();
}

@Component({
  selector: 'app-google-address-auto-complete',
  standalone: true,
  templateUrl: './google-address-auto-complete.html',
  styleUrl: './google-address-auto-complete.css',
  imports: [BaseSignalInput],
})
export class GoogleAddressAutoComplete
  extends BaseFormControl<GoogleAddressValue | null>
  implements AfterViewInit
{
  @ViewChild('autocompleteContainer')
  private autocompleteContainer!: ElementRef<HTMLDivElement>;

  readonly label = input('');
  readonly helpText = input('');
  readonly placeholder = input('Search for a location');
  readonly valueChange = output<GoogleAddressValue | null>();
  readonly locationSelected = output<google.maps.places.Place>();

  private readonly platformId = inject(PLATFORM_ID);

  private readonly autocomplete =
    signal<google.maps.places.PlaceAutocompleteElement | null>(null);
  /** Skip clear-on-empty while the widget catches up after a place selection. */
  private suppressEmptyClear = false;

  constructor() {
    super();

    effect(() => {
      const autocomplete = this.autocomplete();

      if (!autocomplete) {
        return;
      }

      const blocked = this.disabled() || this.readonly();

      autocomplete.toggleAttribute('disabled', blocked);
      autocomplete.style.pointerEvents = blocked ? 'none' : '';
      autocomplete.style.opacity = blocked ? '0.6' : '1';
    });

    effect(() => {
      const autocomplete = this.autocomplete();
      const current = this.value();
      if (!autocomplete) {
        return;
      }

      if (!current) {
        return;
      }

      const label = widgetLabelFromValue(current);
      if (!label) {
        return;
      }

      if (autocomplete.value.trim() === label) {
        return;
      }

      autocomplete.value = label;
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    configureGoogleMaps();
    const { PlaceAutocompleteElement } = await importLibrary('places');

    const autocomplete = new PlaceAutocompleteElement();

    this.autocomplete.set(autocomplete);

    autocomplete.placeholder = this.placeholder();

    autocomplete.style.display = 'block';
    autocomplete.style.width = '100%';
    autocomplete.style.setProperty('color-scheme', 'light');

    autocomplete.style.backgroundColor = '#ffffff';
    autocomplete.style.color = '#111827';

    autocomplete.style.border = '1px solid #d1d5db';
    autocomplete.style.borderRadius = '10px';
    autocomplete.style.fontSize = '14px';
    autocomplete.style.transition = 'border-color 150ms ease';

    autocomplete.addEventListener('gmp-select', async (event) => {
      const placePrediction = (event as google.maps.places.PlacePredictionSelectEvent)
        .placePrediction;

      const display = predictionListDisplay(placePrediction);
      const place = placePrediction.toPlace();

      await place.fetchFields({
        fields: [
          'displayName',
          'formattedAddress',
          'shortFormattedAddress',
          'location',
          'id',
        ],
      });

      const address = placeFullAddressText(place, display);
      const nextValue: GoogleAddressValue = {
        display: display || undefined,
        address,
        latitude: place.location?.lat() ?? null,
        longitude: place.location?.lng() ?? null,
        placeId: place.id ?? null,
      };

      const widgetLabel = widgetLabelFromValue(nextValue);
      this.suppressEmptyClear = true;
      if (widgetLabel) {
        autocomplete.value = widgetLabel;
      }

      this.setValue(nextValue);
      this.valueChange.emit(nextValue);
      this.locationSelected.emit(place);

      queueMicrotask(() => {
        this.suppressEmptyClear = false;
      });
    });

    autocomplete.addEventListener('input', () => {
      this.clearValueIfEmpty();
    });

    autocomplete.addEventListener('change', () => {
      this.clearValueIfEmpty();
    });

    autocomplete.addEventListener('focusin', () => {
      autocomplete.style.borderColor = 'transparent';
      this.onFocus();
    });

    autocomplete.addEventListener('focusout', () => {
      autocomplete.style.borderColor = '#d1d5db';
      this.clearValueIfEmpty();
      this.onBlur();
    });

    this.autocompleteContainer.nativeElement.appendChild(autocomplete);
  }

  private clearValueIfEmpty(): void {
    if (this.suppressEmptyClear) {
      return;
    }

    const autocomplete = this.autocomplete();
    if (!autocomplete) {
      return;
    }

    if (autocomplete.value.trim() !== '') {
      return;
    }

    if (this.value() === null) {
      return;
    }

    this.setValue(null);
    this.valueChange.emit(null);
  }
}
